import { ChevronRight, Package, Plus } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '../../context/AppStateContext';
import { Badge } from '../common/Badge';
import { PageHeader } from '../common/PageHeader';
import { ui } from '../../styles/ui';
import { getVerificationDisplayStatus } from '../../utils/verificationStatus';

// One product (e.g. "Dolo") registered with a quantity > 1 creates many
// individual Product documents — one per physical unit, each with its own
// barcode — all sharing the same batchNo. This groups them back into one
// row per batch for the list, instead of flooding it with one row per
// individual unit. Clicking a row drills into BatchDetailPage, which lists
// (and lets you bulk-print) the individual units.
const groupByBatch = (products) => {
  const groups = new Map();
  for (const product of products) {
    const key = product.batchNo || product._id;
    if (!groups.has(key)) {
      groups.set(key, { batchNo: product.batchNo, sample: product, units: [] });
    }
    groups.get(key).units.push(product);
  }
  return Array.from(groups.values());
};

const BatchStatusSummary = ({ units }) => {
  const verifiedCount = units.filter((u) => getVerificationDisplayStatus(u) === 'verified').length;
  const rejectedCount = units.filter((u) => getVerificationDisplayStatus(u) === 'rejected').length;
  if (units.length === 1) {
    const status = getVerificationDisplayStatus(units[0]);
    const tone = status === 'verified' ? 'success' : status === 'rejected' ? 'danger' : status === 'in_progress' ? 'info' : 'warning';
    const label = status === 'verified' ? 'Verified' : status === 'rejected' ? 'Rejected' : status === 'in_progress' ? 'In Progress' : 'Pending';
    return <Badge tone={tone}>{label}</Badge>;
  }
  return (
    <div className="text-xs text-gray-500 dark:text-gray-400">
      <span className="font-medium text-emerald-600 dark:text-emerald-400">{verifiedCount}</span> verified
      {rejectedCount > 0 && <> &middot; <span className="font-medium text-red-600 dark:text-red-400">{rejectedCount}</span> rejected</>}
      {' '}of {units.length}
    </div>
  );
};

export const ProductsManagement = () => {
  const navigate = useNavigate();
  const { products, loadProducts } = useAppState();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Refetch every time this tab is opened, not just once at login.
  useEffect(() => {
    loadProducts(undefined, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesSearch = searchTerm === '' ||
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.batchNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.barcode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = statusFilter === 'all' || getVerificationDisplayStatus(product) === statusFilter;
      return matchesSearch && matchesFilter;
    });
  }, [products, searchTerm, statusFilter]);

  const batches = useMemo(() => groupByBatch(filteredProducts), [filteredProducts]);

  const goToBatch = (batchNo) => navigate(`/admin/products/batch/${encodeURIComponent(batchNo)}`);

  return (
    <div className="space-y-4">
      <PageHeader title="Products Management" subtitle="Manage products and verification status" />

      <div className="flex flex-col sm:flex-row gap-2.5">
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`flex-1 ${ui.input}`}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={ui.input + ' sm:w-44'}>
          <option value="all">All Status</option>
          <option value="verified">Verified</option>
          <option value="in_progress">In Progress</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
        </select>
        <button
          onClick={() => navigate('/admin/products/add')}
          className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white transition-all duration-150 active:scale-[0.97] whitespace-nowrap"
        >
          <Plus className="h-4 w-4" />
          Add Product
        </button>
      </div>

      {/* Desktop table */}
      <div className={`hidden md:block ${ui.card} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className={ui.tableHeadCell}>Product Details</th>
                <th className={ui.tableHeadCell}>Batch</th>
                <th className={ui.tableHeadCell}>Units</th>
                <th className={ui.tableHeadCell}>Status</th>
                <th className={`${ui.tableHeadCell} text-right`}></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {batches.map(({ batchNo, sample, units }) => (
                <tr key={batchNo} className={`${ui.tableRow} cursor-pointer`} onClick={() => goToBatch(batchNo)}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-9 h-9 bg-emerald-600 rounded-lg flex items-center justify-center mr-3 shrink-0">
                        <Package className="h-[18px] w-[18px] text-white" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">{sample.name}</div>
                        <div className="mt-0.5">
                          <Badge tone="info">{sample.category}</Badge>
                        </div>
                        <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">Added {new Date(sample.createdAt).toLocaleDateString()}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-700 dark:text-gray-300">{batchNo}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">${sample.price} &middot; {sample.unit}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">{units.length}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">barcode{units.length === 1 ? '' : 's'}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <BatchStatusSummary units={units} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <ChevronRight className="h-4 w-4 text-gray-400 inline-block" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-3">
        {batches.map(({ batchNo, sample, units }) => (
          <div key={batchNo} className={`${ui.card} p-3.5 cursor-pointer`} onClick={() => goToBatch(batchNo)}>
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center min-w-0">
                <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center mr-2.5 shrink-0">
                  <Package className="h-4 w-4 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{sample.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Batch {batchNo} &middot; {units.length} barcode{units.length === 1 ? '' : 's'}
                  </div>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
            </div>
            <BatchStatusSummary units={units} />
          </div>
        ))}
      </div>

    </div>
  );
};
